import type { GutBodyConnectionsOrgan } from '@repo/types';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { organAnchorId } from 'views/Results/utils/organs';
import useCurrentKitAndTinyAccount from './useCurrentKitAndTinyAccount';

/** Where the organ was tapped; also the `source` the tap event reports. */
export type SelectOrganSource = 'home_card' | 'conditions_home';

/**
 * Picking an organ on a body map or organ list. The home card navigates to the
 * organ's section on the conditions page; the conditions page scrolls to it.
 */
export function useSelectOrgan(source: SelectOrganSource) {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();

  return useCallback(
    (organ: GutBodyConnectionsOrgan) => {
      trackEvent('Conditions Organ Tapped', {
        organ_system: organ.lookup_code,
        source,
      });

      const anchorId = organAnchorId(organ.lookup_code);

      if (source === 'home_card') {
        navigate(`/results/${currentKitId}/conditions/metrics#${anchorId}`, {
          state: { source },
        });
        return;
      }

      document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth' });
    },
    [currentKitId, navigate, source, trackEvent],
  );
}

export default useSelectOrgan;
